import {CustomFilterProps, useGridFilter} from 'ag-grid-react';
import {Chips, NumberInput, Separator, Slider} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';

import {useCallback, useState} from 'react';

import {isNil} from 'ramda';
import {isArray, isString} from 'ramda-adjunct';

import {formatFileSize, suffixTestId, TestIdProps} from 'shared/web';

import {useFilterOnChipsChange} from '../../hooks/useFilterOnChipsChange';
import {useSliderFilter} from '../../hooks/useSliderFilter';
import {useSliderTicks} from '../../hooks/useSliderTicks';
import {QuickFilters} from '../../types/Api';
import {getFileSizeUnit} from '../../utils/getFileSizeUnit';

const DISPLAY_DECIMALS = 2;
const DISPLAY_DECIMALS_MULTIPLIER = 10 ** DISPLAY_DECIMALS;

const roundForDisplay = (value: number) =>
  Math.round(value * DISPLAY_DECIMALS_MULTIPLIER) / DISPLAY_DECIMALS_MULTIPLIER;

export type FileSizeSliderFilterProps = {
  min: number;
  max: number;
  minOpen: boolean;
  maxOpen: boolean;
  stepSize: number | null;
  steps: number[];
  markerSize: number | null;
  markers: number[];
  inputsVisible: boolean;
  isDisabled: boolean;
} & CustomFilterProps &
  QuickFilters &
  TestIdProps;

export function FileSizeSliderFilter({
  model,
  onModelChange,
  min,
  max,
  stepSize,
  steps,
  markerSize,
  markers,
  inputsVisible,
  isDisabled,
  quickFilters,
  ...props
}: FileSizeSliderFilterProps) {
  const ticks = useSliderTicks(min, max, markerSize, markers);
  const valueFormatter = useCallback((value: number) => formatFileSize(value, 0), []);
  const {label: inputUnit, factor: unitFactor} = getFileSizeUnit(max);

  const bytesToInputValue = (bytes: number | null | undefined) =>
    isNil(bytes) ? null : roundForDisplay(bytes / unitFactor);
  const inputValueToBytes = (value: number | null) =>
    isNil(value) ? null : Math.round(value * unitFactor);

  const [localSliderValue, setLocalSliderValue] = useState<[number | null, number | null] | null>([
    min,
    max,
  ]);

  const [filterValue, setFilterValue] = useSliderFilter({model, onModelChange, min, max});

  const {onChipsChange} = useFilterOnChipsChange({
    setFilterValue: (newValue) => {
      setFilterValue(newValue);
      setLocalSliderValue([min, max]);
    },
    defaultValue: null,
  });

  useGridFilter({
    doesFilterPass: () => true,
    afterGuiAttached: () => setLocalSliderValue(filterValue),
  });

  const onSliderValueChange = (value: [number | null, number | null] | null) => {
    setLocalSliderValue(value);
    setFilterValue(value);
  };

  const onFromInputChange = (value: number | null) => {
    const valueToSet = value === null || isNaN(value) ? null : value;

    if (isNil(filterValue)) {
      onSliderValueChange([valueToSet, null]);
    }

    if (isArray(filterValue)) {
      const isFromNilOrLowestPossible = isNil(valueToSet) || valueToSet === min;
      const isToNilOrLowestPossible = isNil(filterValue?.[1]) || filterValue?.[1] === max;

      if (isFromNilOrLowestPossible && isToNilOrLowestPossible) {
        onSliderValueChange(null);
      } else {
        onSliderValueChange([valueToSet, filterValue?.[1] === 0 ? null : filterValue?.[1]]);
      }
    }

    if (isString(filterValue)) {
      onSliderValueChange([valueToSet, max]);
    }
  };

  const onToInputChange = (value: number | null) => {
    const valueToSet = value === null || isNaN(value) ? null : value;

    if (isNil(filterValue)) {
      onSliderValueChange([null, valueToSet]);
    }

    if (isArray(filterValue)) {
      const isFromNilOrLowestPossible = isNil(filterValue?.[0]) || filterValue?.[0] === min;
      const isToNilOrLowestPossible = isNil(valueToSet) || valueToSet === max;

      if (isFromNilOrLowestPossible && isToNilOrLowestPossible) {
        onSliderValueChange(null);
      } else {
        onSliderValueChange([filterValue?.[0] === 0 ? null : filterValue?.[0], valueToSet]);
      }
    }

    if (isString(filterValue)) {
      onSliderValueChange([min, valueToSet]);
    }
  };

  const isQuickFilterValue = isString(model) ? [model] : null;

  const isSliderValue = isArray(localSliderValue)
    ? [localSliderValue?.[0] ?? min, localSliderValue?.[1] ?? max]
    : [min, max];

  const isFromValue = isArray(filterValue) ? filterValue?.[0] : null;
  const isToValue = isArray(filterValue) ? filterValue?.[1] : null;

  return (
    <VStack spacing={5}>
      {quickFilters && quickFilters.length > 0 && (
        <>
          <HStack>
            <Chips
              isDisabled={isDisabled}
              value={isQuickFilterValue}
              options={quickFilters}
              onChange={onChipsChange}
              isMultiple={false}
              data-testid={suffixTestId('file-size-slider-filter-quickFilters', props)}
              isDeselectable
            />
          </HStack>
          <Separator />
        </>
      )}
      <Slider
        isDisabled={isDisabled}
        isRange
        min={min}
        max={max}
        step={stepSize ?? steps ?? 1}
        value={isSliderValue as [number, number]}
        allowMoveStart
        onChange={onSliderValueChange}
        formatValue={valueFormatter}
        ticks={ticks}
        onlyEdges={true}
        displayValue="auto"
        data-testid={suffixTestId('file-size-slider-filter-slider', props)}
      />
      {inputsVisible && (
        <HStack spacing={2}>
          <NumberInput
            value={bytesToInputValue(isFromValue)}
            onChange={(value) => onFromInputChange(inputValueToBytes(value))}
            suffix={inputUnit}
            decimalPlaces={DISPLAY_DECIMALS}
            isDisabled={isDisabled}
            data-testid={suffixTestId('file-size-slider-filter-from', props)}
          />
          <NumberInput
            value={bytesToInputValue(isToValue)}
            onChange={(value) => onToInputChange(inputValueToBytes(value))}
            suffix={inputUnit}
            decimalPlaces={DISPLAY_DECIMALS}
            isDisabled={isDisabled}
            data-testid={suffixTestId('file-size-slider-filter-to', props)}
          />
        </HStack>
      )}
    </VStack>
  );
}
